import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  WritableSignal
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { map, Observable, startWith, Subject, takeUntil } from 'rxjs';
import { CountiesService, CountyResponse, LoadingSpinnerService, SchoolsService, ToastMessageService } from '@k12/app-services';
import { AppInsightsService } from '@k12/error_handler';
import { InstitutionDto, SeverityLevel, SvgIcon, ToastMessageType, UpdateInstitutionModel } from '@k12/models';
import { PhonePipe } from '@k12/pipes';
import { MonthDayDateAdapter, MONTH_DAY_FORMAT } from '../../date-adapters/month-day-date-adapter';
import { State, states } from '../basic-info/basic-info-const';

const NORTH_CAROLINA = 'NC';
const LOAD_ERROR = 'Failed to load institution information.';
const LOAD_ERROR_TOAST = `${LOAD_ERROR} Please try again later.`;
const SAVE_ERROR = 'Failed to save institution information.';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-institution-summary',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    MatAutocompleteModule,
    MatButtonModule,
    MatDatepickerModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule
  ],
  templateUrl: './institution-summary.component.html',
  styleUrls: ['./institution-summary.component.scss'],
  providers: [
    PhonePipe,
    { provide: DateAdapter, useClass: MonthDayDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MONTH_DAY_FORMAT }
  ]
})
export class InstitutionSummaryComponent implements OnInit, OnDestroy {
  @Input() institutionId: string | null = null;

  /** Emitted after a successful save so the page can refresh anything derived from the institution. */
  @Output() saved = new EventEmitter<InstitutionDto>();

  institution: WritableSignal<InstitutionDto | null> = signal(null);
  isEditing = signal(false);
  counties: CountyResponse[] = [];
  filteredCounties!: Observable<CountyResponse[]>;
  filteredStates!: Observable<State[]>;
  form!: FormGroup;

  readonly editIcon: SvgIcon = SvgIcon.Edit;
  readonly states: State[] = states;

  private readonly appInsightsService = inject(AppInsightsService);
  private readonly countiesService = inject(CountiesService);
  private readonly fb = inject(FormBuilder);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly ngUnsubscribe: Subject<void> = new Subject();
  private readonly phonePipe = inject(PhonePipe);
  private readonly schoolsService = inject(SchoolsService);
  private readonly toastService = inject(ToastMessageService);

  ngOnInit(): void {
    this.buildForm();
    this.loadInstitution();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  get isNorthCarolina(): boolean {
    return this.resolveStateCode(this.form?.get('state')?.value) === NORTH_CAROLINA;
  }

  get address(): string {
    const data = this.institution();
    if (!data) {
      return '-';
    }

    const cityStateZip = [data.city, data.state, data.zip].filter((part) => !!part).join(', ');
    const lines = [data.address1, data.address2, cityStateZip].filter((line) => !!line);

    return lines.length > 0 ? lines.join('<br>') : '-';
  }

  get hdmaName(): string {
    const hdma = this.institution()?.hdma;
    if (!hdma) {
      return '-';
    }

    return [hdma.firstName, hdma.lastName].filter((part) => !!part?.trim()).join(' ') || '-';
  }

  get hdmaPhone(): string {
    const hdma = this.institution()?.hdma;
    const raw = hdma?.phone?.trim();
    if (!raw) {
      return '-';
    }

    const phone = this.phonePipe.transform(raw);
    const extension = hdma?.phoneExtension?.trim();

    return extension ? `${phone} ext. ${extension}` : phone;
  }

  onEdit(): void {
    this.patchForm();
    this.isEditing.set(true);
  }

  onCancel(): void {
    this.isEditing.set(false);
    this.patchForm();
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.institutionId) {
      this.handleError('Institution ID is required', 'Unable to save institution information.');
      return;
    }

    this.loadingSpinnerService.showSpinner();

    this.schoolsService
      .updateInstitution(this.institutionId, this.buildPayload())
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          this.loadingSpinnerService.hideSpinner();

          if (!response.success || !response.entity) {
            const message = response.message || SAVE_ERROR;
            this.handleError(message, message);
            return;
          }

          this.institution.set(response.entity);
          this.isEditing.set(false);
          this.patchForm();
          this.toastService.showToast(ToastMessageType.Success, 'Institution information saved successfully');
          this.saved.emit(response.entity);
        },
        error: (error) => {
          this.loadingSpinnerService.hideSpinner();
          const message = error?.error?.message || SAVE_ERROR;
          this.handleError(message, message);
        }
      });
  }

  /** FEIN is stored as XX-XXXXXXX, so the dash is inserted as the admin types. */
  formatFein(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '').substring(0, 9);

    if (value.length > 2) {
      value = `${value.substring(0, 2)}-${value.substring(2)}`;
    }

    this.form.patchValue({ federalEmployerID: value }, { emitEvent: false });
  }

  displayStateName(state: State | string): string {
    return typeof state === 'string' ? state : state?.name || '';
  }

  private buildForm(): void {
    this.form = this.fb.group({
      address1: ['', Validators.required],
      address2: [''],
      city: ['', Validators.required],
      county: [''],
      state: [null as State | string | null, Validators.required],
      zip: ['', Validators.required],
      federalEmployerID: ['', [Validators.required, Validators.pattern(/^\d{2}-\d{7}$/)]],
      fiscalYearEndDate: [null as Date | null]
    });

    this.filteredStates = this.form.get('state')!.valueChanges.pipe(
      startWith(this.form.get('state')!.value),
      map((value) => this.filterStates(value))
    );

    this.filteredCounties = this.form.get('county')!.valueChanges.pipe(
      startWith(this.form.get('county')!.value ?? ''),
      map((value) => this.filterCounties(value ?? ''))
    );

    this.form
      .get('state')!
      .valueChanges.pipe(startWith(this.form.get('state')!.value), takeUntil(this.ngUnsubscribe))
      .subscribe((value) => this.applyCountyRules(this.resolveStateCode(value)));
  }

  /** Counties are only tracked for North Carolina, matching the school address form. */
  private applyCountyRules(stateCode: string | undefined): void {
    const county = this.form.get('county')!;

    if (stateCode === NORTH_CAROLINA) {
      county.setValidators([Validators.required]);

      if (this.counties.length === 0) {
        this.loadCounties();
      }
    } else {
      county.clearValidators();
      county.setValue('', { emitEvent: false });
    }

    county.updateValueAndValidity({ emitEvent: false });
  }

  private loadCounties(): void {
    this.countiesService
      .getCountiesByState(NORTH_CAROLINA)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (counties) => {
          this.counties = counties;
          this.form.get('county')!.updateValueAndValidity({ emitEvent: true });
        },
        error: () => {
          this.counties = [];
        }
      });
  }

  private loadInstitution(): void {
    if (!this.institutionId) {
      this.handleError('Institution ID is required', LOAD_ERROR);
      return;
    }

    this.loadingSpinnerService.showSpinner();

    this.schoolsService
      .getInstitution(this.institutionId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          this.loadingSpinnerService.hideSpinner();

          if (response.success && response.entity) {
            this.institution.set(response.entity);
            this.patchForm();
            return;
          }

          this.handleError(response.message || LOAD_ERROR, LOAD_ERROR_TOAST);
        },
        error: (error) => {
          this.loadingSpinnerService.hideSpinner();
          this.handleError(error?.error?.message || LOAD_ERROR, LOAD_ERROR_TOAST);
        }
      });
  }

  private patchForm(): void {
    const data = this.institution();
    if (!data) {
      return;
    }

    this.form.patchValue({
      address1: data.address1 ?? '',
      address2: data.address2 ?? '',
      city: data.city ?? '',
      county: data.county ?? '',
      state: this.states.find((s) => s.code === data.state || s.name === data.state) ?? data.state ?? null,
      zip: data.zip ?? '',
      federalEmployerID: data.federalEmployerID ?? '',
      fiscalYearEndDate: data.fiscalYearEndDate ? new Date(data.fiscalYearEndDate) : null
    });

    this.applyCountyRules(this.resolveStateCode(this.form.get('state')!.value));
  }

  private buildPayload(): UpdateInstitutionModel {
    const value = this.form.getRawValue();
    const fiscalYearEndDate: Date | null = value.fiscalYearEndDate;

    return {
      businessAddressId: this.institution()?.businessAddressId ?? null,
      address1: value.address1,
      address2: value.address2,
      city: value.city,
      county: value.county,
      state: this.resolveStateCode(value.state) ?? '',
      zip: value.zip,
      federalEmployerID: value.federalEmployerID,
      fiscalYearEndDate: fiscalYearEndDate ? fiscalYearEndDate.toISOString() : null
    };
  }

  private resolveStateCode(value: State | string | null | undefined): string | undefined {
    if (!value) {
      return undefined;
    }

    if (typeof value !== 'string') {
      return value.code;
    }

    return this.states.find((s) => s.code === value || s.name === value)?.code;
  }

  private filterStates(value: State | string | null): State[] {
    const filterValue = (typeof value === 'string' ? value : value?.name || '').toLowerCase();
    return this.states.filter((state) => state.name.toLowerCase().includes(filterValue));
  }

  private filterCounties(value: string): CountyResponse[] {
    const filterValue = value.toLowerCase();
    return this.counties.filter((county) => county.name.toLowerCase().includes(filterValue));
  }

  private handleError(errorMessage: string, toastMessage: string): void {
    this.appInsightsService.log(new Error(errorMessage), SeverityLevel.error);
    this.toastService.showToast(ToastMessageType.Error, toastMessage);
  }
}
